/**
 * The envelope page's window control (TMMAPP-0114 A3), shared by the Register and P&L toolbars: the
 * virtual account P&L's chips (`RangeChips`) — Year to date (the default) · Last 12 months · This
 * month · Last month · Last year — and Custom, with From / To date fields, validated client-side
 * (`from ≤ to`). The dates in force read beside the chips.
 */
import { useId, useState } from 'react';
import { Box, TextField, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RangeChips } from '@/features/virtualAccount/RangeChips';
import { presetRange, type RangePreset } from '@/features/virtualAccount/downloadRange';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { isBadDates, rangeDates, rangePresetOf, type EnvelopeRange } from './envelopeRange';

interface EnvelopeRangeControlProps {
  value: EnvelopeRange;
  onChange: (next: EnvelopeRange) => void;
  /** The civil today in the viewer's zone. */
  today: string;
  /**
   * A register search on the default period looks at all time (TMMAPI-0165): the Year-to-date chip
   * reads "All time" and the resolved dates are hidden. Choosing another preset still narrows.
   */
  allTime?: boolean;
}

export function EnvelopeRangeControl({
  value,
  onChange,
  today,
  allTime = false,
}: EnvelopeRangeControlProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const errorId = useId();
  const preset = rangePresetOf(value, today);
  // Custom stays open once chosen, even on a range a preset also names.
  const [custom, setCustom] = useState(preset === null);
  const [draft, setDraft] = useState<EnvelopeRange>(() => {
    const dates = rangeDates(value, today);
    return { from: dates?.from ?? value.from, to: dates?.to ?? value.to };
  });
  // A change from outside (Clear filters, Back) re-seeds the control from the URL.
  const [seen, setSeen] = useState(value);
  if (seen.from !== value.from || seen.to !== value.to) {
    setSeen(value);
    const own = value.from === draft.from && value.to === draft.to;
    if (!own) {
      const next = rangeDates(value, today);
      setDraft({ from: next?.from ?? value.from, to: next?.to ?? value.to });
      setCustom(preset === null);
    }
  }
  const bad = custom && isBadDates(draft.from, draft.to);
  const dates = rangeDates(value, today);

  function choose(next: RangePreset) {
    setCustom(false);
    const [from, to] = presetRange(next, today);
    setDraft({ from, to });
    onChange(next === 'ytd' ? { from: '', to: '' } : { from, to });
  }

  // Every edit is the URL's: a bad range there requests nothing (and disables the download) until
  // it is fixed.
  function editDraft(next: EnvelopeRange) {
    setDraft(next);
    onChange(next);
  }

  return (
    <Box data-envelope-range="">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <RangeChips
          preset={custom ? null : preset}
          custom={custom}
          onPreset={choose}
          onCustom={() => setCustom(true)}
          labels={allTime ? { ytd: t('search.allTime') } : undefined}
        />
        {!allTime && dates?.from !== undefined && dates.to !== undefined && (
          <Typography
            variant="caption"
            data-window-resolved=""
            sx={{ color: tokens.textLabel('surface'), fontVariantNumeric: 'tabular-nums' }}
          >
            {t('envelope.windowResolved', {
              from: formatCivilDate(dates.from, locale),
              to: formatCivilDate(dates.to, locale),
            })}
          </Typography>
        )}
      </Box>
      {custom && (
        <Box
          sx={{
            mt: 1.5,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 200px))' },
            gap: 1.5,
          }}
        >
          <TextField
            type="date"
            size="small"
            label={t('envelope.windowFrom')}
            value={draft.from}
            onChange={(event) => editDraft({ ...draft, from: event.target.value })}
            error={bad}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: bad ? { 'aria-describedby': errorId } : {},
            }}
          />
          <TextField
            type="date"
            size="small"
            label={t('envelope.windowTo')}
            value={draft.to}
            onChange={(event) => editDraft({ ...draft, to: event.target.value })}
            error={bad}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: bad ? { 'aria-describedby': errorId } : {},
            }}
          />
        </Box>
      )}
      {bad && (
        <Typography id={errorId} role="alert" sx={{ mt: 1, fontSize: 12, color: tokens.errorText }}>
          {t('envelope.windowInvalid')}
        </Typography>
      )}
    </Box>
  );
}
